/**
 * Maximum file/recording length (PLAN.md §2, §16.1 memory budget).
 * 15 minutes by default; 5 minutes on devices that are likely to run out of memory.
 */

export const FULL_CAP_MINUTES = 15;
export const REDUCED_CAP_MINUTES = 5;

export interface LengthCapInput {
  /** navigator.deviceMemory in GB (Chromium only), undefined elsewhere */
  deviceMemory: number | undefined;
  maxTouchPoints: number;
  /** min(screen.width, screen.height) in CSS px */
  shortScreenSide: number;
  /** false if a probe allocation failed */
  probeAllocationOk: boolean;
}

export interface LengthCap {
  minutes: number;
  reason: string;
}

export function decideLengthCap(i: LengthCapInput): LengthCap {
  if (!i.probeAllocationOk) {
    return { minutes: REDUCED_CAP_MINUTES, reason: 'memory allocation test failed' };
  }
  if (i.deviceMemory !== undefined && i.deviceMemory <= 4) {
    return { minutes: REDUCED_CAP_MINUTES, reason: `device memory ${i.deviceMemory} GB` };
  }
  // Phone-sized touch screen (iPhone etc.): per-tab memory limit is low.
  if (i.maxTouchPoints > 0 && i.shortScreenSide < 600) {
    return { minutes: REDUCED_CAP_MINUTES, reason: 'phone-sized touch device' };
  }
  return { minutes: FULL_CAP_MINUTES, reason: 'default' };
}

let cached: LengthCap | undefined;

export function currentLengthCap(): LengthCap {
  if (cached) return cached;
  let probeAllocationOk = true;
  try {
    // One minute of mono 48 kHz Float32 (~11 MB): cheap, but fails on very constrained tabs.
    const probe = new Float32Array(48000 * 60);
    probe[probe.length - 1] = 1;
  } catch {
    probeAllocationOk = false;
  }
  const nav = navigator as Navigator & { deviceMemory?: number };
  cached = decideLengthCap({
    deviceMemory: nav.deviceMemory,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    shortScreenSide: Math.min(screen.width, screen.height),
    probeAllocationOk,
  });
  return cached;
}
